import { Button } from '@progress/kendo-react-buttons';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Checkbox, ColorPicker, Switch, TextBox } from '@progress/kendo-react-inputs';
import { useState } from 'react';
import { useBlocker } from 'react-router';
import { v4 } from 'uuid';
import { AddOutline, ArrowDownOutline, ArrowUpOutline, SaveOutline, TrashOutline } from '../../../../../../icons/icons';
import {
    SettingsWorldClockDateFormatOptions,
    SettingsWorldClockItem,
    SettingsWorldClockTimeFormatOptions,
    clockTimeZones
} from '../../../../../../types/clocks';
import { DropdownListWithValueField } from '../../../../../components/inputs/DropdownListWithValueField';
import { useAppDispatch, useAppSelector } from '../../../../../hooks/store-hooks';
import { setSetting } from '../../../../../state/slices/settings-slice';
import { SettingsNavTitle, useSettingsPageWidth } from '../../../context/SettingsNavContext';

const MAX_CLOCK_COUNT = 8;
type Item = SettingsWorldClockItem & { id: string };

export function ClockSettings() {
    useSettingsPageWidth('700px');
    const clocksSettings = useAppSelector((x) => x.settings.worldClocks);
    const [enabled, setEnabled] = useState(clocksSettings.enabled);
    const [barColor, setBarColor] = useState(clocksSettings.barColor);
    const [timeColor, setTimeColor] = useState(clocksSettings.timeColor);
    const [dateColor, setDateColor] = useState(clocksSettings.dateColor);
    const [nameColor, setNameColor] = useState(clocksSettings.nameColor);
    const [dateFormat, setDateFormat] = useState(clocksSettings.dateFormat);
    const [timeFormat, setTimeFormat] = useState(clocksSettings.timeFormat);
    const [addOffsetToName, setAddOffsetToName] = useState(clocksSettings.addOffsetToName);
    const [timeZoneUppercase, setTimeZoneUppercase] = useState(clocksSettings.timeZoneUppercase);
    const [clocks, setClocks] = useState(() => clocksSettings.clocks.map((x) => ({ ...x, id: v4() })));
    const [edited, setEdited] = useState(false);
    const blocker = useBlocker(edited);
    const dispatch = useAppDispatch();

    const handleSave = () => {
        setEdited(false);
        dispatch(
            setSetting('worldClocks', {
                enabled,
                barColor,
                timeColor,
                dateColor,
                nameColor,
                dateFormat,
                timeFormat,
                addOffsetToName,
                timeZoneUppercase,
                clocks: clocks.map((x) => ({
                    timeZoneName: x.timeZoneName,
                    useCustomName: x.useCustomName,
                    customName: x.customName
                }))
            })
        );
        if (blocker.state === 'blocked') {
            blocker.proceed();
        }
    };

    return (
        <>
            {blocker.state === 'blocked' && (
                <Dialog title="Clocks" onClose={() => blocker.reset()}>
                    <div>Do you want to save pending changes?</div>
                    <DialogActionsBar>
                        <Button themeColor={'primary'} onClick={handleSave}>
                            Yes
                        </Button>
                        <Button fillMode={'outline'} onClick={() => blocker.proceed()}>
                            No
                        </Button>
                    </DialogActionsBar>
                </Dialog>
            )}
            <SettingsNavTitle>
                <div className="d-flex justify-content-between align-items-center">
                    <div>Clocks</div>
                    <Button svgIcon={SaveOutline} fillMode="outline" disabled={!edited} onClick={handleSave}>
                        Save
                    </Button>
                </div>
            </SettingsNavTitle>

            <div className="h-100 k-card p-3 overflow-auto">
                <div className="d-flex justify-content-between gap-2 pb-2 border-bottom">
                    <div>Enabled</div>
                    <Switch
                        checked={enabled}
                        onChange={(e) => {
                            setEdited(true);
                            setEnabled(e.value);
                        }}
                    />
                </div>

                <div className="d-flex gap-2 justify-content-between mt-2">
                    <div>
                        <div>Bar color</div>
                        <ColorPicker
                            views={['palette', 'gradient']}
                            paletteSettings={{ palette: colorPickerColors }}
                            value={barColor}
                            onChange={(e) => {
                                setEdited(true);
                                setBarColor(e.value);
                            }}
                        />
                    </div>
                    <div>
                        <div>Time color</div>
                        <ColorPicker
                            views={['palette', 'gradient']}
                            paletteSettings={{ palette: colorPickerColors }}
                            value={timeColor}
                            onChange={(e) => {
                                setEdited(true);
                                setTimeColor(e.value);
                            }}
                        />
                    </div>
                    <div>
                        <div>Date color</div>
                        <ColorPicker
                            views={['palette', 'gradient']}
                            paletteSettings={{ palette: colorPickerColors }}
                            value={dateColor}
                            onChange={(e) => {
                                setEdited(true);
                                setDateColor(e.value);
                            }}
                        />
                    </div>
                    <div>
                        <div>Name color</div>
                        <ColorPicker
                            views={['palette', 'gradient']}
                            paletteSettings={{ palette: colorPickerColors }}
                            value={nameColor}
                            onChange={(e) => {
                                setEdited(true);
                                setNameColor(e.value);
                            }}
                        />
                    </div>
                </div>

                <div className="mt-2 secondary-text">Date format</div>
                <div>
                    <DropdownListWithValueField
                        data={SettingsWorldClockDateFormatOptions}
                        value={dateFormat}
                        onChange={(e) => {
                            setEdited(true);
                            setDateFormat(e.value);
                        }}
                    />
                </div>

                <div className="mt-2 secondary-text">Time format</div>
                <div>
                    <DropdownListWithValueField
                        data={SettingsWorldClockTimeFormatOptions}
                        value={timeFormat}
                        onChange={(e) => {
                            setEdited(true);
                            setTimeFormat(e.value);
                        }}
                    />
                </div>

                <div className="mt-2">
                    <Checkbox
                        label="Append offset to time zone name"
                        value={addOffsetToName}
                        onChange={(e) => {
                            setEdited(true);
                            setAddOffsetToName(e.value);
                        }}
                    />
                </div>

                <div className="mt-2">
                    <Checkbox
                        label="Display time zone name and day/month in upper case"
                        value={timeZoneUppercase}
                        onChange={(e) => {
                            setEdited(true);
                            setTimeZoneUppercase(e.value);
                        }}
                    />
                </div>

                <div className="d-flex align-items-end justify-content-between py-2">
                    <div>Clocks (max {MAX_CLOCK_COUNT}):</div>
                    <Button
                        svgIcon={AddOutline}
                        themeColor="primary"
                        disabled={clocks.length >= MAX_CLOCK_COUNT}
                        onClick={() => {
                            setEdited(true);
                            setClocks([
                                ...clocks,
                                {
                                    id: v4(),
                                    timeZoneName: 'Etc/GMT',
                                    useCustomName: false,
                                    customName: null
                                }
                            ]);
                        }}
                    />
                </div>

                <div className="d-flex flex-column gap-3">
                    {clocks.map((clock, i) => (
                        <ClockEditView
                            key={clock.id}
                            clock={clock}
                            canMoveDown={i < clocks.length - 1}
                            canMoveUp={i > 0}
                            index={i}
                            onMove={(id, dir) => {
                                setEdited(true);
                                const idx = clocks.findIndex((x) => x.id === id);
                                const copy = [...clocks];
                                const diff = dir === 'up' ? -1 : +1;
                                const item = copy[idx];
                                copy[idx] = copy[idx + diff];
                                copy[idx + diff] = item;
                                setClocks(copy);
                            }}
                            onDelete={(id) => {
                                setEdited(true);
                                setClocks(clocks.filter((x) => x.id !== id));
                            }}
                            onEdit={(item) => {
                                setEdited(true);
                                const idx = clocks.findIndex((x) => x.id === item.id);
                                const copy = [...clocks];
                                copy[idx] = {
                                    ...clocks[idx],
                                    ...item
                                };
                                setClocks(copy);
                            }}
                        />
                    ))}
                    {clocks.length === 0 && <div className="secondary-text">No clocks defined.</div>}
                </div>
            </div>
        </>
    );
}

function ClockEditView(props: {
    clock: Item;
    index: number;
    canMoveUp: boolean;
    canMoveDown: boolean;
    onDelete: (id: string) => void;
    onMove: (id: string, dir: 'up' | 'down') => void;
    onEdit: (item: Partial<Item> & { id: string }) => void;
}) {
    const customDisplayName = props.clock.customName;
    const useCustomName = props.clock.useCustomName;
    const timeZone = props.clock.timeZoneName;

    return (
        <div className="k-card p-2">
            <div className="d-flex gap-2 align-items-top justify-content-between">
                <div>Clock {props.index + 1}</div>
                <div className="d-flex gap-1">
                    <Button
                        svgIcon={ArrowUpOutline}
                        fillMode="outline"
                        onClick={() => props.onMove(props.clock.id, 'up')}
                        disabled={!props.canMoveUp}
                    />
                    <Button
                        svgIcon={ArrowDownOutline}
                        fillMode="outline"
                        onClick={() => props.onMove(props.clock.id, 'down')}
                        disabled={!props.canMoveDown}
                    />
                    <Button svgIcon={TrashOutline} themeColor="error" fillMode="outline" onClick={() => props.onDelete(props.clock.id)} />
                </div>
            </div>
            <div>Time zone:</div>
            <div className="d-flex gap-2">
                <DropdownListWithValueField
                    data={clockTimeZones}
                    value={timeZone}
                    onChange={(e) => props.onEdit({ id: props.clock.id, timeZoneName: e.value })}
                />
            </div>
            <div className="pt-2">
                <Checkbox
                    label="Custom display name"
                    checked={useCustomName}
                    onChange={(e) => props.onEdit({ id: props.clock.id, useCustomName: e.value })}
                />
            </div>

            {useCustomName && (
                <TextBox
                    className="mt-2"
                    placeholder="Name"
                    value={customDisplayName ?? ''}
                    onChange={(e) => props.onEdit({ id: props.clock.id, customName: e.value as string })}
                />
            )}
        </div>
    );
}

const colorPickerColors: string[] = [
    'rgb(255,0,0)',
    'rgb(0,0,255)',
    'rgb(0,255,0)',
    'rgb(255,255,0)',
    'rgb(139,26,194)',
    'rgb(255,255,255)',
    'rgb(200,0,0)',
    'rgb(175,175,0)'
];
